import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import { COMPANY, SERVICES } from "../../lib/site-data";
import { toast } from "sonner";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Please fill in your name and message.");
      return;
    }
    const subject = encodeURIComponent(
      `Quote Request from ${form.name}${form.service ? " — " + form.service : ""}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nService: ${form.service}\n\nMessage:\n${form.message}\n\n— Sent via taylormadeplumbing.com`
    );
    window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
    toast.success("Opening your email app — your draft is ready to send!");
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Big CTA banner */}
        <div className="relative rounded-3xl overflow-hidden bg-[#1D2B53] grain">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "28px 28px"
          }}></div>
          <div className="relative px-6 sm:px-10 lg:px-14 py-12 lg:py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FBBF24] mb-3">
                Need Plumbing Help?
              </div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
                Need Plumbing Help? Call Today.
              </h3>
              <p className="mt-3 text-white/70 text-lg max-w-xl">
                One quick call gets you a real person, a fair quote, and a plumber on the way.
              </p>
            </div>
            <a
              href={COMPANY.phoneLink}
              data-testid="contact-big-cta-call"
              className="inline-flex items-center gap-3 px-8 py-5 rounded-2xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-lg shadow-2xl shadow-blue-500/30 transition-all hover:-translate-y-0.5 shrink-0"
            >
              <Phone className="h-6 w-6" />
              {COMPANY.phone}
            </a>
          </div>
        </div>

        {/* Contact info + Form */}
        <div className="mt-16 grid lg:grid-cols-5 gap-10 lg:gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">
                Get In Touch
              </div>
              <h2
                data-testid="contact-heading"
                className="font-display text-3xl sm:text-4xl font-black text-[#1D2B53] tracking-tight leading-tight"
              >
                Request a free estimate.
              </h2>
              <p className="mt-3 text-base text-gray-600">
                Fill out the form and your email app will open with the details ready to send to Jason directly.
              </p>
            </div>

            <div className="space-y-4">
              <a
                href={COMPANY.phoneLink}
                data-testid="contact-info-phone"
                className="flex items-start gap-4 p-5 rounded-2xl bg-[#F3F4F6] hover:bg-[#2563EB] hover:text-white transition-colors group"
              >
                <div className="h-11 w-11 rounded-xl bg-white group-hover:bg-white/15 flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-[#2563EB] group-hover:text-white" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1D2B53]/60 group-hover:text-white/70">
                    Call or Text
                  </div>
                  <div className="font-display text-lg font-bold text-[#1D2B53] group-hover:text-white">
                    {COMPANY.phone}
                  </div>
                </div>
              </a>

              <a
                href={COMPANY.emailLink}
                data-testid="contact-info-email"
                className="flex items-start gap-4 p-5 rounded-2xl bg-[#F3F4F6] hover:bg-[#1D2B53] hover:text-white transition-colors group"
              >
                <div className="h-11 w-11 rounded-xl bg-white group-hover:bg-white/15 flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-[#2563EB] group-hover:text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1D2B53]/60 group-hover:text-white/70">
                    Email
                  </div>
                  <div className="font-display text-base font-bold text-[#1D2B53] group-hover:text-white break-all">
                    {COMPANY.email}
                  </div>
                </div>
              </a>

              <div
                data-testid="contact-info-location"
                className="flex items-start gap-4 p-5 rounded-2xl bg-[#F3F4F6]"
              >
                <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-[#2563EB]" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1D2B53]/60">
                    Based In
                  </div>
                  <div className="font-display text-lg font-bold text-[#1D2B53]">
                    {COMPANY.location}
                  </div>
                </div>
              </div>

              <div
                data-testid="contact-info-hours"
                className="flex items-start gap-4 p-5 rounded-2xl bg-[#F3F4F6]"
              >
                <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0">
                  <Clock className="h-5 w-5 text-[#2563EB]" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1D2B53]/60">
                    Hours
                  </div>
                  <div className="font-display text-base font-bold text-[#1D2B53]">
                    Mon–Sat 7a–7p
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-[#F3F4F6] rounded-3xl p-6 sm:p-10 border border-gray-100"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#1D2B53] mb-2">
                  Your Name *
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  data-testid="contact-form-name"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-[#1D2B53] placeholder:text-gray-400 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition"
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#1D2B53] mb-2">
                  Phone
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  data-testid="contact-form-phone"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-[#1D2B53] placeholder:text-gray-400 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition"
                  placeholder="(555) 555-5555"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#1D2B53] mb-2">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-form-email"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-[#1D2B53] placeholder:text-gray-400 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#1D2B53] mb-2">
                  Service Needed
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  data-testid="contact-form-service"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-[#1D2B53] focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition"
                >
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.title} value={s.title}>
                      {s.title}
                    </option>
                  ))}
                  <option value="Other">Other / Not sure</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-bold uppercase tracking-wider text-[#1D2B53] mb-2">
                Tell us about the job *
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={5}
                data-testid="contact-form-message"
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-[#1D2B53] placeholder:text-gray-400 focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition"
                placeholder="Briefly describe what's going on..."
              />
            </div>

            <button
              type="submit"
              data-testid="contact-form-submit"
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-base shadow-xl shadow-blue-500/30 transition-all hover:-translate-y-0.5"
            >
              <Send className="h-5 w-5" />
              Send Request
            </button>
            <p className="mt-3 text-xs text-gray-500">
              Submitting opens your email app so you can review &amp; send directly to {COMPANY.email}.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
